import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { API_BASE } from "./lib/apiBase";
import { getLoginUrl } from "./const";
import "./index.css";

// Canonical host is .com — .co.in custom domains serve this same SPA until DNS/Pages settle.
if (typeof window !== "undefined") {
  const host = window.location.hostname.toLowerCase();
  if (host === "purvaswanlake.co.in" || host === "www.purvaswanlake.co.in") {
    const dest = new URL(window.location.href);
    dest.hostname = "purvaswanlake.com";
    window.location.replace(dest.toString());
  }
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // Pass the current page path so the user lands back here after login
  const returnPath = window.location.pathname;
  window.location.href = getLoginUrl(returnPath !== "/" ? returnPath : undefined);
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${API_BASE}/api/trpc`,
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Load analytics script if configured
if (typeof window !== "undefined") {
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;
  if (endpoint && websiteId) {
    const script = document.createElement("script");
    script.defer = true;
    script.src = endpoint + "/umami";
    script.setAttribute("data-website-id", websiteId);
    document.head.appendChild(script);
  }
}

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
